import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BALANCE_MESSAGE,
  ROOM_JOIN_REFUSAL_CODE,
  isRoomJoinRefusal,
  type BalanceMessage,
  type RoomJoinRefusal,
  type SeatReservation,
} from "@wr/contracts";
import {
  ROOM_AUTH_ERROR,
  type GameRoomStateView,
  type GameSeatView,
  type RoomPhaseView,
} from "@wr/game-core";
import { t } from "@wr/i18n";
import { ApiRequestError, postRoomLeave } from "../lib/api";
import { wsToken } from "../lib/auth";
import {
  joinGameRoom,
  leaveConsented,
  restartReconnect,
  type GameRoomConnection,
} from "../lib/game-client";
import type { ConnectionState } from "../lib/connection";

/**
 * The room screen's controller — one socket, one seat, one screen (S4-7).
 *
 * ── It lives under `SeatProvider`, not under the screen ────────────────────
 *
 * LOCKED rule 21 (§16c A18): `SeatProvider` is the **only** writer of seat state
 * in `apps/web`, and this hook is what it now writes from. That is not
 * bookkeeping — the socket has to outlive the navigation that creates it. The
 * lobby joins, *then* routes to `/room/:id` (Ivy §2's mount table), so a
 * controller owned by the room screen would be constructed after the seat it is
 * meant to hold already exists, and every reconnect would be a fresh join.
 *
 * ── The snapshot is a copy, and it carries its own arrival time ────────────
 *
 * Colyseus state is mutable and not React-friendly, so every patch is copied
 * into an immutable snapshot. It carries `receivedAt` — the client clock at the
 * moment the patch landed — because **every countdown on this screen is computed
 * from an absolute instant corrected against `serverTime`** (§17 note 1). A
 * client that received "18 seconds left" and then slept for 30 renders a
 * countdown wrong by exactly the sleep; a client that received "ends at
 * 12:03:14Z, and it is now 12:02:56Z on the server" does not.
 *
 * ── What it deliberately does not do ───────────────────────────────────────
 *
 * **No money.** The pre-game leave's refund is `POST /rooms/:id/leave`'s, and
 * the mid-game forfeit is the *server's* reading of a consented socket close.
 * This hook sends exactly one message — `ready`, with no payload — and reads
 * everything else.
 *
 * **One amendment to that, ruled by A129 §2.2 (`CONNECT-WINDOW-LEAVE`, T-474):
 * `detach()` now CALLS that leave route on the one path where nothing else
 * can.** Between `POST /rooms/join` returning 200 and the socket landing there
 * is no `Room` object, so the consented close has nothing to close and the seat
 * — already charged (rule 40's CHECK makes a fee-bearing reservation without an
 * entry debit unstorable) — was abandoned in silence. The hook still decides no
 * money: it sends the same signal the `Leave` button sends, to the same route,
 * and the route decides the refund. What changed is *that* the signal is sent,
 * not who computes it.
 *
 * **No optimistic anything.** §11.4: a `ready` that the server did not accept
 * must never render `ready_done`, so `ready()` sends and waits for the seat's
 * own flag to come back. A failed leave leaves the player on the screen.
 */

/** A plain, immutable copy of the decoded room state. Satisfies the view. */
export interface GameRoomSnapshot extends GameRoomStateView {
  seats: GameSeatView[];
  results: { decided: boolean; standings: GameRoomStateView["results"]["standings"] };
  /** The client clock when this patch landed — the skew reference (§17 note 1). */
  receivedAt: number;
}

export interface EnterRoomInput {
  /** Verbatim from `POST /rooms/join` (§16 A8/A11). Never rebuilt, never re-keyed. */
  reservation: SeatReservation;
  /** The join envelope's flag. Necessary for the redirect toast, not sufficient. */
  redirected: boolean;
  /**
   * The state the tile the player tapped was **displayed as** — the second half
   * of §14's toast guard, which became load-bearing on 2026-07-29 (lobby §20.2).
   *
   * `null` for `Play again` and `Join again`: they are not tile taps, they set
   * no rendered expectation, and both are LOCKED never to raise the toast.
   */
  fromTileState: string | null;
  /** `room_configs.id`, so `Play again` knows what to re-join (§8.4). */
  roomConfigId: string;
}

/**
 * **The game plugin's window onto the room — the one game-shaped seam (S5-4).**
 *
 * Everything here is `unknown` on purpose. The controller is platform code and
 * platform code that can name a `round` block is platform code that knows what a
 * word is; Ivy §16 item 5's boundary test is *"if the wrapper imports anything
 * from `game-types`, PRD §10's central claim is broken"*, and the same argument
 * applies to the socket layer. So the state crosses as bytes and **the plugin
 * does the cast** (`game/word-search/read-state.ts` is the only file that does).
 *
 * `subscribe` fires **immediately** with the current state and then once per
 * patch, which is what lets a plugin mount mid-round and paint the board it
 * arrived into without a separate "give me the state" call.
 */
export interface GamePluginBridge {
  subscribe: (listener: (state: unknown) => void) => () => void;
  send: (type: string, payload: unknown) => void;
  onMessage: (type: string, handler: (payload: unknown) => void) => () => void;
}

export interface GameRoomController {
  connection: ConnectionState;
  snapshot: GameRoomSnapshot | null;
  /** The Colyseus session id — the only identifier this client has for itself. */
  sessionId: string | null;
  /** `room_configs.id` behind the held seat, for `Play again` (§8.4). */
  roomConfigId: string | null;
  /**
   * **`game_types.engine_key`, which is also the Colyseus room name** — the key
   * S5-6's registry resolves a plugin from.
   *
   * It arrives on the seat reservation (`reservation.name`, set by the api from
   * `game_types.engine_key`) and is therefore known **at join**, before the first
   * patch — which is what makes Ivy §5.10's *"the client prefetches the game
   * chunk at `ready_check` at the latest"* a prefetch rather than a guess.
   */
  engineKey: string | null;
  /** The plugin boundary. See `GamePluginBridge`. */
  game: GamePluginBridge;
  /**
   * The redirect toast is owed **once**, on mount (§14). Read and cleared by the
   * screen so a re-render cannot raise it twice and a reconnect cannot raise it
   * again — a toast that re-appears is a message the player did not earn.
   */
  redirectToast: boolean;
  consumeRedirectToast: () => void;
  /** `room.caught_up`, owed once after a reconnect that crossed a phase (§11.4). */
  caughtUp: boolean;
  consumeCaughtUp: () => void;
  /** The SDK gave up. The seat is gone; no further state will arrive. */
  seatLost: boolean;
  /** The phase at the instant of the drop, frozen. See `lib/room.ts`. */
  phaseAtDrop: string | null;
  /** When this client's own grace window ends, in client-clock ms (§11.3). */
  graceExpiresAt: number | null;
  /**
   * **Q12 (Quinn, T-224) — the patience window has run out.**
   *
   * S2 §20.1, LOCKED: *"The gold state must not be permanent. Automatic attempts
   * get a bounded patience window and the coral state must arrive at its end.
   * **45 s ratified.**"* Quinn measured the gold *"Reconnecting."* banner holding
   * for **102 seconds** under a hanging handshake, because the only thing that
   * moves this client off `reconnecting` is the SDK's `onLeave` — and a socket
   * that hangs rather than closing never produces one (the same shape T-223's
   * `restartReconnect` was written for).
   *
   * So the escalation is bound to a **deadline** instead of to an event, and it is
   * deliberately **the same deadline the grace notice counts down**: `drop +
   * reconnect_grace_s`, from `rooms.timing_snapshot`. One number, two renderings —
   * *"back in 0:00 or you forfeit"* and the banner turning coral — rather than a
   * second 45 that could drift from the server's. §9's rule that every rendered
   * number is config-derived is satisfied for free: this one is not a new number
   * at all.
   */
  patienceExpired: boolean;
  error: string | null;
  /**
   * **The server's own `4409` join-refusal reason, narrowed** — the input `T9`
   * is selected from (`SOCKET-REFUSAL-COPY`, T-698).
   *
   * It exists because `error` does not: `error` is a free-form string that no
   * rendered node reads (T-665 measured that, and it is still true), so a
   * refusal the server named precisely arrived at the client and was thrown
   * away. This field is the same fact **typed**, and `@wr/contracts`'
   * `Record<RoomJoinRefusal, …>` map downstream is what makes a sixth
   * server-side refusal a compile error instead of a silent `T6`.
   *
   * **Sticky by design — Ivy `G.7` A1 and A2.** Nothing clears it but `reset()`,
   * which is a deliberate navigation. The refusal self-clears server-side in
   * ~20–23 s and this client neither watches for that nor reacts to it; a later
   * transport drop does not un-answer the player's question either.
   */
  joinRefusal: RoomJoinRefusal | null;
  /**
   * Consume a seat reservation.
   *
   * **Resolves `true` only when the socket is live** — `false` for a refused or
   * failed join, and `false` for a join that resolved after the caller had
   * already detached (the stale resolution A129 §2.2 constraint 2 names). It
   * never rejects; the reason, in words, is on `error`.
   *
   * The boolean is what lets the screen fall through to the A42 door on a failed
   * warm entry instead of parking on T6 (A129 §2.1 constraint 9's client half,
   * T-472 finding F1).
   */
  enter: (input: EnterRoomInput) => Promise<boolean>;
  /**
   * **The frozen room's recovery — S2 §20.1's `Retry`, restarted at the socket.**
   *
   * `true` means the attempt sequence was restarted against a seat this client
   * still holds. `false` means there was nothing to restart — no socket, or a
   * healthy one — and the caller must not treat this as the recovery it offered
   * (room spec §9.8 / S2 §20.4: a control that cannot act is furniture).
   *
   * It sends **no message and moves no money**, and it cannot be a consented
   * leave: `restartReconnect` closes with `MAY_TRY_RECONNECT`, which is the code
   * the SDK's own retries close with. `leave(true)` — the code that forfeits — is
   * reached only from `detach()`, and this path does not touch it.
   */
  reconnect: () => boolean;
  /** A29: one-way, idempotent, and the server decides whether it lands. */
  ready: () => void;
  /**
   * A83 / Ivy §4.4: `ready`'s twin. **`false` means the message could not be
   * transmitted** — the caller toasts `room.bots.offerFailed` and leaves the
   * button enabled. It never means the server refused: a tap the offer does not
   * authorise is a silent no-op there, by design.
   */
  acceptBotOffer: () => boolean;
  /** Consented socket close. The money is the API's; this frees the seat. */
  leave: () => Promise<void>;
  /**
   * **The connect-window reservation has been released by another route.**
   *
   * The screen's own pre-game leave (§6.5, and T-474's `seat_reserved` recovery)
   * posts to the *same* api route `detach()` would post to, and it does so on the
   * player's behalf with a response it needs — the canonical `roomId` for the
   * spent-room set, and the "never navigate away from a room the server still
   * thinks you are in" LOCK. Telling the controller keeps the invariant true:
   * **one leave signal per abandoned reservation.** Without it the navigation
   * that follows a successful leave sends a second, redundant one.
   *
   * Idempotent, synchronous, and never sends anything itself.
   */
  clearPendingLeave: () => void;
  /** Drop everything. Called when the player leaves the room route for good. */
  reset: () => void;
}

/**
 * **The connect window's leave signal — A129 §2.2, verbatim mechanism.**
 *
 * > *"On detach with no room, call the api's leave route. It already does
 * > exactly this job and was written for exactly this case."*
 *
 * `service.ts`'s own comment on that route: *"A pre-game leave very often
 * happens before the WebSocket has arrived — a tap that changed its mind."* It
 * runs `markParticipantLeft` + `refundParticipantEntry` in one transaction,
 * publishes the balance event after the commit, gives the Colyseus hold back so
 * the tile is not locked (`reservationCancelled`), and is idempotent under LOCKED
 * rule 27's `refund:{roomId}:{playerId}` key. **No new route, no new field, no
 * new refusal code** — the id sent is the reservation's own `roomId`, which is
 * Colyseus's, and `findRoomByEitherId` accepts it.
 *
 * **Fire-and-forget, and that is a decision rather than laziness.** By the time
 * this runs the player has left the screen: there is nothing to put an error on,
 * and the fallbacks below it are real — the room's own in-band sweep (T-472) and
 * then the five-minute cron. A failure here costs the old slow path, never the
 * money.
 *
 * **Mid-game is not this function's business** and is refused server-side
 * (`game_in_progress`, A129 §2.2 constraint 5): a forfeit stays the socket's, and
 * a socket is exactly what this path does not have.
 */
async function releaseConnectWindowSeat(roomId: string): Promise<void> {
  try {
    await postRoomLeave(roomId);
  } catch (err) {
    // The seat survives us: T-472's in-band sweep collects it on the room's own
    // clock, and the cron behind that. Loud enough to find, quiet enough to
    // leave a player who is already somewhere else alone.
    console.warn("[room] the connect-window leave could not be sent", err);
  }
}

/**
 * **`room.caught_up` does not fire on arrival at a terminal** (§8.6, §9.7):
 * arriving at a result — or at "this one doesn't count" — is not catching up, it
 * is the ending.
 *
 * Module scope since T-566, because the comparison moved onto the `ROOM_STATE`
 * path and that path runs on every patch. Same three members, same rule.
 */
const CATCH_UP_TERMINAL_PHASES: ReadonlySet<RoomPhaseView> = new Set([
  "results",
  "cancelled",
  "closed",
]);

function copySnapshot(room: GameRoomConnection): GameRoomSnapshot {
  const state = room.state;
  const seats: GameSeatView[] = [];
  state.seats?.forEach((seat) => {
    seats.push({
      seatIndex: seat.seatIndex,
      teamIndex: seat.teamIndex,
      /**
       * **The seat's side, copied — not rendered** (S11 spec 3 / S12 R1). R1 is
       * *"the table and the assignment, no new chrome"*: R2's results split and
       * R3's room chrome are the first surfaces to read it, and this copy lands
       * with the server field so the client half of the wire is complete from
       * the moment the server sends it.
       *
       * **`?? -1` fails closed, for `botOfferPossible`'s reason** (A114 §6): a
       * new `web` against an old `game` receives no such field, and `-1` is the
       * schema's own "no side" sentinel — a room whose sides are unknown renders
       * as a room with no sides, never as a room where everyone is side 0. The
       * `-1 → null` map is `@wr/contracts`' `sideIndexFromWire` and belongs to
       * the reader, not to this copy, exactly as `prizeCoins`' sentinel does.
       */
      sideIndex: seat.sideIndex ?? -1,
      colorIndex: seat.colorIndex,
      sessionId: seat.sessionId,
      nickname: seat.nickname,
      avatarUrl: seat.avatarUrl,
      status: seat.status,
      ready: seat.ready,
      canReady: seat.canReady,
      seatOutcome: seat.seatOutcome,
      /**
       * §17 note 9's `me.prizeCoins`, copied verbatim including its `-1`
       * sentinel — `lib/room.ts`'s `myPrizeCoins` is the only place that maps
       * it to `null`, so this copy does not interpret it.
       */
      prizeCoins: seat.prizeCoins,
      roundScore: seat.roundScore,
      totalScore: seat.totalScore,
      /**
       * **§16h A81's three seat fields, copied — not rendered.** BOT-4 owns
       * every surface: the `BOT` chip, the offer card, the confirmation state
       * and the stats note. This snapshot copy lands with the server fields so
       * the client half of the wire is complete from the moment the server
       * sends them, and so a `?? false` default lives in exactly one place
       * rather than at each future reader.
       *
       * The defaults matter on one real path: a client running this build
       * against a room started by an older process receives no such field, and
       * *"the row does not render as a bot"* is the only safe reading of an
       * absent flag (Ivy §3.6 item 5 — disclosure is never inferred).
       */
      isBot: seat.isBot ?? false,
      botOfferAvailable: seat.botOfferAvailable ?? false,
      botOfferAccepted: seat.botOfferAccepted ?? false,
      /**
       * **A114 §6, LOCKED — `botOfferPossible` fails closed: absent ⇒ `false`,
       * never "unknown, therefore probably yes".** A new `web` against an old
       * `game` receives no such field; the `?? false` here is the single place
       * that default lives. Under §22.19.9's inverted trigger this means the
       * alone-note may render in a mis-sequenced pre-offer window — a *true*
       * sentence shown unnecessarily, which §22.19.9 rules licensed — and the
       * predicate's `!botOfferAvailable` belt keeps it off the screen beside a
       * live offer card.
       */
      botOfferPossible: seat.botOfferPossible ?? false,
    });
  });

  const standings: {
    seatIndex: number;
    place: number;
    totalScore: number;
    prizeCoins: number;
    isWinner: boolean;
  }[] = [];
  state.results?.standings?.forEach((standing) => {
    standings.push({
      seatIndex: standing.seatIndex,
      place: standing.place,
      totalScore: standing.totalScore,
      prizeCoins: standing.prizeCoins,
      isWinner: standing.isWinner,
    });
  });

  return {
    roomId: state.roomId ?? "",
    roomConfigId: state.roomConfigId ?? "",
    phase: state.phase ?? "",
    cancelReason: state.cancelReason ?? "",
    roundIndex: state.roundIndex ?? 0,
    roundCount: state.roundCount ?? 0,
    entryFeeCoins: state.entryFeeCoins ?? 0,
    prizeCoins: state.prizeCoins ?? 0,
    prizeDistributionKind: state.prizeDistributionKind ?? "",
    minPlayers: state.minPlayers ?? 0,
    maxPlayers: state.maxPlayers ?? 0,
    teamCount: state.teamCount ?? 0,
    teamSize: state.teamSize ?? 0,
    gameTypeName: state.gameTypeName ?? "",
    gameModeName: state.gameModeName ?? "",
    serverTime: state.serverTime ?? "",
    readyDeadlineAt: state.readyDeadlineAt ?? "",
    fillDeadlineAt: state.fillDeadlineAt ?? "",
    nextRoundAt: state.nextRoundAt ?? "",
    roundEndsAt: state.roundEndsAt ?? "",
    reconnectGraceS: state.reconnectGraceS ?? 0,
    fillTimeoutMs: state.fillTimeoutMs ?? 0,
    seats,
    results: { decided: state.results?.decided ?? false, standings },
    receivedAt: Date.now(),
  };
}

export function useGameRoom(onBalance?: (message: BalanceMessage) => void): GameRoomController {
  const roomRef = useRef<GameRoomConnection | null>(null);
  const joiningRef = useRef(false);
  /**
   * **The reservation this client is charged for but has no socket for yet** —
   * Colyseus's `roomId`, held from the moment `enter()` is handed a reservation
   * a 200 join produced, and released the instant the socket lands.
   *
   * A129 §2.2 constraint 1: *"fire only when a seat may exist — the join returned
   * 200 and no socket was attached. A leave for a join that never succeeded is a
   * wasted call; a leave for a join in flight is the case this is for."* It is
   * deliberately **kept** when the join fails: that is precisely the
   * charged-but-never-connected seat (T-473's four production orphans), and the
   * player is sitting on the failure screen still holding it.
   */
  const pendingReservation = useRef<string | null>(null);
  /**
   * **The in-flight join's epoch — A129 §2.2 constraint 2, which is its own
   * defect.**
   *
   * `enter()` had a re-entrancy flag but no *cancellation*: after `reset()` ran,
   * a pending `joinGameRoom` still resolved and assigned `roomRef.current`, so a
   * player who navigated away during connect could end up holding a live socket
   * for a room they had left — an orphan the ghost sweep will **not** collect,
   * because `joined_at` is set by then. Every `detach()` bumps this; a join whose
   * epoch has moved leaves the socket it just opened and assigns nothing.
   */
  const generation = useRef(0);

  const [connection, setConnection] = useState<ConnectionState>("idle");
  const [snapshot, setSnapshot] = useState<GameRoomSnapshot | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [roomConfigId, setRoomConfigId] = useState<string | null>(null);
  const [engineKey, setEngineKey] = useState<string | null>(null);
  const [redirectToast, setRedirectToast] = useState(false);
  const [caughtUp, setCaughtUp] = useState(false);
  const [seatLost, setSeatLost] = useState(false);
  const [phaseAtDrop, setPhaseAtDrop] = useState<string | null>(null);
  const [graceExpiresAt, setGraceExpiresAt] = useState<number | null>(null);
  /** Q12 — see `patienceExpired` on the controller. Armed by the deadline below. */
  const [patienceExpired, setPatienceExpired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joinRefusal, setJoinRefusal] = useState<RoomJoinRefusal | null>(null);
  /**
   * Narrows a socket close into this vocabulary, and **only** into it: an
   * unknown reason, or a reason on any other code, returns `null` and the
   * shipped `T6` behaviour is untouched. `code` is the discriminator's first
   * half — `ROOM_AUTH_ERROR`'s codes carry one refusal each and never reach
   * here.
   */
  const asJoinRefusal = useCallback((code: unknown, reason: unknown): RoomJoinRefusal | null => {
    if (code !== ROOM_JOIN_REFUSAL_CODE) return null;
    return isRoomJoinRefusal(reason) ? reason : null;
  }, []);

  /** The phase the client last *witnessed*, for §11.4's `room.caught_up`. */
  const witnessedPhase = useRef<string | null>(null);
  /**
   * **The pre-drop phase, held until the first `ROOM_STATE` after a reconnect
   * can be compared against it** (T-566, R1 — the fix branch of Dex's
   * `RECONNECT-CAUGHTUP-CENSUS`).
   *
   * ── Why this ref exists at all, which is the whole bug ─────────────────────
   *
   * `@colyseus/sdk@0.17.43` invokes `onReconnect` from inside its **`JOIN_ROOM`**
   * branch (`src/Room.ts`: the `else` of `if (this.joinedAtTime === 0)`, right
   * after the `✅ reconnection successful!` line). Nothing in that branch touches
   * the serializer — only `Protocol.ROOM_STATE` and `ROOM_STATE_PATCH` call
   * `setState`/`patch` — and the server does not send the full state until it
   * receives the client's **JOIN_ROOM acknowledgement** (`@colyseus/core`
   * `Room.ts`, `code === Protocol.JOIN_ROOM && client.state === ClientState.JOINING`
   * → `sendFullState(client)`). So `room.state` inside `onReconnect` is still the
   * **pre-drop** state.
   *
   * The old code compared the held phase against a snapshot taken there, i.e.
   * **the pre-drop phase against itself**, so `setCaughtUp(true)` was
   * unreachable and the flag was dead in production — driven on staging at
   * T-559 across a 26 s outage spanning `ready_check → round`: the SDK logged
   * `✅ reconnection successful!`, the surface flipped to `Round 1 of 3`, and no
   * toast ever appeared.
   *
   * **A ref, not state**, for `graceDeadline`'s reason: the comparison is armed
   * in one SDK callback and settled in another, and a state read inside a
   * listener registered at join would be the value at registration time.
   *
   * **Non-latching reads inside `onReconnect` are NOT this bug** and are left
   * alone: `adopt()` there re-publishes the stale snapshot, and the very next
   * `onStateChange` overwrites it. Only a **one-shot** taken from that state is
   * unrecoverable, because it never gets a second chance. That distinction is
   * the mechanism this fix is keyed on.
   */
  const catchUpFrom = useRef<string | null>(null);
  /**
   * **The phase this client last received in a server STATE message** — written
   * only from `onStateChange`, and the only thing `catchUpFrom` is ever armed
   * from (T-566).
   *
   * ── Why not `witnessedPhase`, which sounds like the same thing ─────────────
   *
   * `witnessedPhase` is **also seeded at join**, and that seed is not a phase
   * the server ever sent: `Client.consumeSeatReservation` resolves on the SDK's
   * `onJoin`, which is invoked from the **same `JOIN_ROOM` branch** described
   * above, so the serializer at that moment holds a freshly constructed root
   * schema — its field defaults, not the room. Arming from it would make the
   * one narrow sequence *"join → drop before the first `ROOM_STATE` → reconnect
   * → `ROOM_STATE`"* raise a *"You're back — here's where things are"* toast at
   * a player who had not yet been anywhere. Unreachable before this ticket
   * (nothing raised at all) and cheap to close now, so it is closed here rather
   * than left as a new false positive of my own making.
   *
   * **`witnessedPhase`'s join seed is deliberately left alone**: `phaseAtDrop`
   * reads it and feeds `lib/room.ts`'s T3/T4 classification and `graceMessage`'s
   * rendered copy, which is a different mechanism and a different row. Filed as
   * a watch-item, not fixed here.
   */
  const phaseFromServer = useRef<string | null>(null);
  /**
   * **The one grace deadline for the current drop** (T-217, Quinn B4). A ref
   * because `onDrop` fires once per *failed reconnection attempt* and must read
   * "is a window already open" synchronously — a state read in that closure
   * would be the value at the time the listener was registered, i.e. always
   * `null`, which is the bug.
   */
  const graceDeadline = useRef<number | null>(null);
  /**
   * The latest snapshot, readable from a socket callback. `onDrop` needs
   * `reconnectGraceS` and there is no re-render between the drop and the read.
   */
  const snapshotRef = useRef<GameRoomSnapshot | null>(null);
  /** Held in a ref so a new callback identity does not re-open the socket. */
  const onBalanceRef = useRef(onBalance);
  onBalanceRef.current = onBalance;
  /**
   * The connection state, readable from a **stable** callback.
   *
   * `reconnect()` has to know whether the transport is actually down, and it must
   * keep one identity for the life of the screen: it is published to the chrome
   * through `useReportTransport`, whose effect re-publishes on every change of
   * the retry's identity. A `useCallback` over `connection` would therefore make
   * the banner re-publish on every drop and reconnect.
   */
  const connectionRef = useRef<ConnectionState>("idle");
  connectionRef.current = connection;

  /**
   * **The plugin bridge** (S5-4). Every member has one identity for the life of
   * the hook, because the board's effects depend on them: a `subscribe` that
   * changed identity per patch would tear down and re-register the game's own
   * listener on every frame of a round.
   *
   * The listener set outlives any single `Room` object, which is what makes a
   * cold rejoin (A42 mints a new socket) invisible to a mounted plugin — it keeps
   * receiving patches from whichever room the controller currently holds.
   */
  const stateListeners = useRef(new Set<(state: unknown) => void>());
  const subscribeGameState = useCallback((listener: (state: unknown) => void) => {
    stateListeners.current.add(listener);
    // Fire once with what we already have, so a plugin mounting mid-round paints
    // the board it arrived into rather than waiting for the next patch.
    if (roomRef.current) listener(roomRef.current.state);
    return () => {
      stateListeners.current.delete(listener);
    };
  }, []);

  const sendGameMessage = useCallback((type: string, payload: unknown) => {
    roomRef.current?.send(type, payload);
  }, []);

  const onGameMessage = useCallback((type: string, handler: (payload: unknown) => void) => {
    const room = roomRef.current;
    if (!room) return () => undefined;
    return room.onMessage(type, handler as (message: unknown) => void);
  }, []);

  const game = useMemo<GamePluginBridge>(
    () => ({ subscribe: subscribeGameState, send: sendGameMessage, onMessage: onGameMessage }),
    [onGameMessage, sendGameMessage, subscribeGameState],
  );

  /**
   * **Give up whatever seat this controller is holding — in either of its two
   * shapes.** T-474 / A129 §2.2 added the second one.
   *
   * `reenteringRoomId` is the *only* reason this takes an argument: `enter()`
   * calls `detach()` in its own preamble, and on the A42 rejoin door that
   * preamble is tearing down the reservation for the **same room it is about to
   * re-enter**. Releasing it there would refund the seat the player is at that
   * moment getting back into. Compared by Colyseus room id because that is the id
   * the reservation carries in both cases; anything else abandons.
   */
  const detach = useCallback(async (reenteringRoomId: string | null = null) => {
    // Every detach cancels an in-flight join (constraint 2). Taken first, so a
    // resolution that races the awaits below is already stale when it lands.
    generation.current += 1;

    const room = roomRef.current;
    roomRef.current = null;

    const reservation = pendingReservation.current;
    const abandoning = reservation !== null && reservation !== reenteringRoomId;
    if (abandoning) pendingReservation.current = null;

    if (!room) {
      /**
       * **The path that cost 5 m 25 s and a locked paid tile** (T-470 §3(c),
       * A129 §2.2 cause B). `if (!room) return` used to be the whole of it, so
       * navigating away during the connect window sent *nothing at all* —
       * `SeatProvider`'s route-change `reset()` did no work, and the seat waited
       * for a five-minute cron. There is no grace window on this path because
       * there is no socket; the api's leave route is the only actor there can be.
       */
      if (abandoning) await releaseConnectWindowSeat(reservation);
      return;
    }
    // A socket makes the reservation moot: from here the consented close is the
    // signal, and the server's own `onLeave` does the pre-game refund.
    pendingReservation.current = null;
    // Listeners first, then the leave: a teardown must not drive the state
    // machine it is tearing down.
    room.removeAllListeners();
    /**
     * The liveness check lives in `leaveConsented` (T-244 N3), because the same
     * ordering bug is reachable from the lobby socket and one rule written twice
     * is one rule that stops holding in one place. Nothing about *this* leave
     * changed: an open socket still gets the consented frame.
     */
    await leaveConsented(room);
  }, []);

  const enter = useCallback(
    async (input: EnterRoomInput): Promise<boolean> => {
      if (joiningRef.current) return false;
      joiningRef.current = true;
      setError(null);
      /**
       * A fresh `enter()` is the PLAYER re-driving, which `A1` names as the only
       * way out: *"the affordances stay live and the PLAYER re-drives; the
       * screen does not re-drive itself."* So a new attempt clears the previous
       * answer — and nothing else in this hook does.
       */
      setJoinRefusal(null);
      setSeatLost(false);
      setPhaseAtDrop(null);
      setGraceExpiresAt(null);
      setPatienceExpired(false);
      setCaughtUp(false);
      setConnection("connecting");
      setRoomConfigId(input.roomConfigId);
      // A failed join must not leave the previous room's engine key standing:
      // the registry would resolve a plugin for a room this client is not in.
      setEngineKey(null);
      witnessedPhase.current = null;
      catchUpFrom.current = null;
      phaseFromServer.current = null;
      graceDeadline.current = null;

      /**
       * **§14's guard, and the flag alone is not it — LOCKED (lobby §20.2).**
       *
       * The toast explains a broken expectation set by a tile: it looked
       * joinable and the seat was gone. Now that the client sends the room id it
       * rendered, a deliberate `QUEUE →` on a `full` tile makes `redirected`
       * true **by construction** — that tile is showing the old room by
       * definition — so firing on the flag alone would apologise for exactly the
       * thing §6.3 says needs no apology. The *displayed* tile state travels
       * with the navigation and is the guard; it is not re-derived here, because
       * re-deriving it would read the tile's state *now* rather than at the tap.
       *
       * `Play again` and `Join again` pass `null` and raise nothing, whatever
       * the flag says (§8.4, §9 T4).
       */
      setRedirectToast(input.redirected && input.fromTileState === "filling");

      /**
       * Our epoch. Seeded here so the `catch` can read it, and **re-taken after
       * the preamble detach**, which bumps the counter: only detaches from then
       * on are cancellations of *this* attempt. There can be no other join in
       * flight to cancel — `joiningRef` above is what guarantees that.
       */
      let myGeneration = generation.current;

      try {
        await detach(input.reservation.roomId);
        myGeneration = generation.current;
        /**
         * **From here the seat exists and this client is charged for it.** The
         * reservation came out of a 200 `POST /rooms/join` (or the A42 rejoin
         * door), so constraint 1's *"a seat may exist"* is satisfied by
         * construction and every exit below owes a leave signal until a socket
         * takes over the job.
         */
        pendingReservation.current = input.reservation.roomId;

        // A fresh access token per join: they live five minutes, so a cached one
        // is a failed join for no reason the player caused.
        const token = await wsToken();
        const room = await joinGameRoom(input.reservation, token);
        /**
         * **The stale resolution** (constraint 2). We were detached while this
         * was in flight, so the socket that just opened belongs to a room the
         * player has left: close it consentedly — which is the server's pre-game
         * leave and its refund — and assign nothing. `detach()` has already sent
         * the HTTP leave for the same seat; both are idempotent under rule 27's
         * key, and whichever arrives second reports a replay.
         */
        if (generation.current !== myGeneration) {
          await leaveConsented(room);
          return false;
        }
        roomRef.current = room;
        // The socket is the signal now (see `detach`).
        pendingReservation.current = null;
        setSessionId(room.sessionId);
        /**
         * The Colyseus room name **is** `game_types.engine_key` (the api reserves
         * with `roomName: target.engineKey`), so the registry's key is known here,
         * at join, before any patch. `room.name` is the SDK's copy of the same
         * `reservation.name`; the reservation is the fallback for a client whose
         * SDK version stopped setting it.
         */
        setEngineKey(room.name || input.reservation.name || null);

        const adopt = () => {
          const next = copySnapshot(room);
          snapshotRef.current = next;
          setSnapshot(next);
          return next;
        };

        room.onStateChange(() => {
          const next = adopt();
          /**
           * **The plugin's patch fan-out** (S5-4). It runs on the same signal as
           * the platform's copy so the board and the wrapper can never render two
           * different patches, and it hands over the **live** state object: the
           * plugin does its own copy, because it is the only thing that knows its
           * own shape (`game/types.ts`).
           */
          for (const listener of stateListeners.current) listener(room.state);
          /**
           * §11.4: a reconnect that crossed a phase raises **one**
           * `room.caught_up` toast, and §12 rule 6 bars motion for anything the
           * player did not witness. `witnessedPhase` is only advanced while the
           * transport is healthy, so a phase that moved during a drop is
           * detectable exactly once, here.
           */
          if (roomRef.current === null) return;

          /**
           * **THE `room.caught_up` COMPARISON, and this is the only place it can
           * correctly happen** (T-566). `onReconnect` armed `catchUpFrom` with
           * the pre-drop phase; this is the **first `ROOM_STATE` after the
           * reconnect**, i.e. the first moment the client holds the phase the
           * room actually moved to. Reading it one message earlier — which is
           * what `onReconnect` did — compares the pre-drop phase with itself.
           *
           * Settled **before** `witnessedPhase` advances, so the two never read
           * each other; `catchUpFrom` is cleared whether or not the toast is
           * owed, because it is a one-shot about *this* reconnect and a later
           * patch is not it.
           */
          const from = catchUpFrom.current;
          if (from !== null) {
            catchUpFrom.current = null;
            if (from !== next.phase && !CATCH_UP_TERMINAL_PHASES.has(next.phase as RoomPhaseView)) {
              setCaughtUp(true);
            }
          }

          phaseFromServer.current = next.phase;
          witnessedPhase.current = next.phase;
        });

        room.onDrop(() => {
          /**
           * **A room we no longer hold cannot open a grace window** (T-217).
           * `onLeave` and `detach()` both clear `roomRef`, and after either the
           * screen is rendering a terminal panel about a seat that has ended —
           * so a late close event must not put the banner back into
           * `reconnecting` or start a countdown over a finished game. Same
           * idiom as `onStateChange`'s guard below.
           */
          if (roomRef.current === null) return;
          setConnection("reconnecting");
          const held = witnessedPhase.current;
          setPhaseAtDrop(held);
          /**
           * §11.3: the grace countdown is `reconnectGraceS` — received **before**
           * the drop, which is the only reason it can be rendered at all — plus
           * the instant the transport died. It keeps running while everything else
           * freezes, because it describes the client's own situation and the
           * client is the authority on how long it has been offline.
           *
           * ── ONE deadline per drop, and that is the whole fix (T-217, Quinn B4) ─
           *
           * **`onDrop` fires once per failed reconnection attempt, not once per
           * drop.** Colyseus 0.17's SDK re-opens the socket on an exponential
           * backoff (`Room.retryReconnection`) and every failed attempt closes
           * with an abnormal code, which re-enters `connection.events.onclose`
           * and re-invokes this signal. Re-seeding here made the clock climb
           * back to 0:45 at t+26s and t+31s of a 45-second window, and read
           * **0:46** — above its own starting value — because the component's
           * sampled `now` was up to 250ms behind the fresh seed. The copy beside
           * that number says *"Back in 0:45 or you forfeit 25 coins"*, so it was
           * a wrong number about coins on the only control a player has during an
           * outage.
           *
           * The deadline therefore lives in a ref and is written **once**, at the
           * first drop, and cleared only by a reconnect, a lost seat or a fresh
           * entry — the three events that end this grace window. `Math.ceil` over
           * a fixed deadline and a monotonically advancing `now` cannot go up.
           */
          if (graceDeadline.current === null) {
            const grace = snapshotRef.current?.reconnectGraceS;
            if (grace !== undefined) {
              graceDeadline.current = Date.now() + grace * 1000;
              setGraceExpiresAt(graceDeadline.current);
            }
          }
        });

        room.onReconnect(() => {
          setConnection("online");
          // The window is over and a *later* drop gets a new one (T-217).
          graceDeadline.current = null;
          setGraceExpiresAt(null);
          setPatienceExpired(false);
          /**
           * **This callback ARMS the `room.caught_up` comparison; it does not
           * make it** (T-566). See `catchUpFrom`: the SDK runs this from its
           * `JOIN_ROOM` branch, so `room.state` here is still the pre-drop
           * state and any decision taken from it compares that phase with
           * itself. The decision is settled on the next `ROOM_STATE`, in
           * `onStateChange` above.
           *
           * `witnessedPhase` is the pre-drop phase and is deliberately **not**
           * advanced here: this callback has no news about the phase. The
           * `adopt()` and the plugin fan-out below stay — they re-publish a
           * snapshot with a fresh `receivedAt` and are corrected by the very
           * next patch, which is the non-latching read `catchUpFrom`'s note
           * distinguishes from the bug.
           *
           * **A second drop before that `ROOM_STATE` arrives re-arms from the
           * same unchanged `phaseFromServer`**, so an outage that takes several
           * attempts still reports the one phase the player last saw — and a
           * client that has never received a state message arms `null` and owes
           * nothing.
           */
          catchUpFrom.current = phaseFromServer.current;
          adopt();
          for (const listener of stateListeners.current) listener(room.state);
        });

        /**
         * The SDK has given up: retries exhausted, or the server closed the
         * seat when the grace window expired. **The snapshot is deliberately
         * kept** — unlike `/dev/room`, which clears it — because this screen has
         * to render a terminal panel *about* that room, and a blank screen is
         * §11.4's explicitly forbidden outcome ("never a blank screen and never
         * a silent redirect").
         */
        room.onLeave(() => {
          roomRef.current = null;
          setConnection("offline");
          setSeatLost(true);
          graceDeadline.current = null;
          setGraceExpiresAt(null);
          /**
           * The seat is gone, so the window it bounded is over. `connection` is
           * already `offline` here and the banner is coral by the ordinary route;
           * leaving this true would make a *later* room's first frame coral.
           */
          setPatienceExpired(false);
        });

        /**
         * **S4-8.** The room forwards this player's own balance channel — one
         * message per ledger write, carrying the row's `balance_after`. The chip
         * is chrome and lives above this hook, so the message is handed straight
         * up rather than held here: a balance that lived in the room controller
         * would die with the room, and the chip does not.
         */
        room.onMessage(BALANCE_MESSAGE, (message: BalanceMessage) =>
          onBalanceRef.current?.(message),
        );

        room.onError((code, message) => {
          /**
           * **A2 — the refusal is recorded, never overwritten.** A second close
           * on the same controller generation cannot replace a refusal already
           * answered: `setJoinRefusal` only ever moves `null -> value`.
           */
          const refusal = asJoinRefusal(code, message);
          if (refusal) setJoinRefusal((current) => current ?? refusal);
          setError(authErrorMessage(code) ?? message ?? null);
        });

        setConnection("online");
        const first = adopt();
        for (const listener of stateListeners.current) listener(room.state);
        witnessedPhase.current = first.phase;
        return true;
      } catch (err) {
        /**
         * A join that **failed** while we were being detached must not paint a
         * room error over whatever the player navigated to; `reset()` has already
         * put this controller back to `idle` and that is the state to leave it in.
         */
        if (generation.current !== myGeneration) return false;
        setConnection("offline");
        {
          const withCode = err as { code?: unknown; message?: unknown };
          const refusal = asJoinRefusal(withCode.code, withCode.message);
          if (refusal) setJoinRefusal((current) => current ?? refusal);
        }
        setError(joinErrorMessage(err));
        /**
         * `pendingReservation` is deliberately **not** cleared here. This is the
         * charged-but-never-connected seat, the player is looking at the failure
         * screen still holding it, and the leave signal is owed on whichever exit
         * they take — the screen's own release control, `Back to the lobby`, or a
         * plain navigation (A129 §2.2).
         */
        return false;
      } finally {
        joiningRef.current = false;
      }
    },
    [detach],
  );

  /**
   * A29: **one-way and idempotent.** There is no un-ready — it is a commitment
   * in a room that is about to start, and an un-ready control is a griefing
   * surface with no legitimate use (§6.3, LOCKED).
   *
   * Nothing optimistic happens here: the button becomes its confirmation only
   * when the seat's own `ready` flag comes back true (§11.4 — *never* show
   * `ready_done` for a ready the server did not accept).
   */
  const ready = useCallback(() => {
    roomRef.current?.send("ready");
  }, []);

  /**
   * **`bot_offer_accept` — the `ready` message's twin** (§16h A83, Ivy §4.4).
   *
   * One-way, idempotent, no payload, and **nothing optimistic**: the card
   * becomes its confirmation only when the seat's own `botOfferAccepted` comes
   * back true. Ivy §4.4 applies §6.5's rule *"never navigate, never assume on a
   * call the server has not confirmed"* to a control that changes **who you are
   * playing**, which is the strongest version of it on this screen.
   *
   * Returns whether the message could be **transmitted**, which is the only
   * failure this transport can report: a socket send has no ack, and the server
   * deliberately answers a tap it will not honour with silence rather than an
   * error (A83 — *"a tap that arrives when the offer is not available is a no-op,
   * not an error: the client did nothing wrong and has nothing to retry"*).
   * `false` is the caller's cue for `room.bots.offer_failed`; it is never a
   * statement that the fill was refused.
   *
   * The `try` is not defensive dressing: `send` throws synchronously when the
   * underlying connection is closing, which is exactly the case where a toast is
   * the right answer.
   */
  const acceptBotOffer = useCallback((): boolean => {
    const room = roomRef.current;
    if (!room) return false;
    try {
      room.send("bot_offer_accept");
      return true;
    } catch (err) {
      console.warn("[room] bot_offer_accept could not be sent", err);
      return false;
    }
  }, []);

  /**
   * **§20.1's `Retry`, and the two guards it needs.**
   *
   * `roomRef.current === null` is *"the SDK has given up or we have detached"* —
   * `onLeave` and `detach()` are the only writers of that null, and after either
   * one the seat is over as far as this socket is concerned. There is nothing to
   * restart, so the answer is `false` and the screen falls back to the rejoin
   * door rather than pressing a dead button.
   *
   * The connection guard is the other half: a **healthy** socket is never closed
   * from here. The call site only offers this while the content is frozen, and
   * this makes that a property of the controller rather than of one caller.
   */
  const reconnect = useCallback((): boolean => {
    const room = roomRef.current;
    if (!room) return false;
    const state = connectionRef.current;
    if (state !== "reconnecting" && state !== "offline") return false;
    return restartReconnect(room);
  }, []);

  const leave = useCallback(async () => {
    await detach();
  }, [detach]);

  /** See `clearPendingLeave` on the controller. One signal per reservation. */
  const clearPendingLeave = useCallback(() => {
    pendingReservation.current = null;
  }, []);

  const reset = useCallback(() => {
    void detach();
    setConnection("idle");
    setSnapshot(null);
    snapshotRef.current = null;
    setSessionId(null);
    setRoomConfigId(null);
    setEngineKey(null);
    setRedirectToast(false);
    setCaughtUp(false);
    setSeatLost(false);
    setPhaseAtDrop(null);
    setGraceExpiresAt(null);
    setPatienceExpired(false);
    setError(null);
    /**
     * `reset()` is the ONE thing that clears a refusal, and it is a deliberate
     * navigation away from the screen — not a timer, not a transport event
     * (Ivy `G.7` A1/A2).
     */
    setJoinRefusal(null);
    witnessedPhase.current = null;
    catchUpFrom.current = null;
    phaseFromServer.current = null;
    graceDeadline.current = null;
  }, [detach]);

  /**
   * **Q12's clock.** One timer, armed on the deadline the drop opened and torn
   * down with it, so a reconnect that lands at t+40 s cancels the escalation it
   * was racing rather than colouring a healthy banner three seconds later.
   *
   * Nothing here retries, gives up or touches the seat: the SDK's own sequence is
   * still running and the server is still holding the seat until *its* window
   * closes. This changes what the screen **says** at the end of the window, which
   * is precisely what §20.1 asks for and all it asks for.
   */
  useEffect(() => {
    if (graceExpiresAt === null) {
      setPatienceExpired(false);
      return;
    }
    const remaining = graceExpiresAt - Date.now();
    if (remaining <= 0) {
      setPatienceExpired(true);
      return;
    }
    const timer = setTimeout(() => setPatienceExpired(true), remaining);
    return () => clearTimeout(timer);
  }, [graceExpiresAt]);

  /**
   * A socket that outlives the app is a seat burning down a grace window — and,
   * since T-474, a *reservation* that outlives the app is a charged seat with no
   * grace window at all. `detach()` answers both.
   */
  useEffect(() => {
    return () => {
      void detach();
    };
  }, [detach]);

  return {
    connection,
    snapshot,
    sessionId,
    roomConfigId,
    engineKey,
    game,
    redirectToast,
    consumeRedirectToast: useCallback(() => setRedirectToast(false), []),
    caughtUp,
    consumeCaughtUp: useCallback(() => setCaughtUp(false), []),
    seatLost,
    phaseAtDrop,
    graceExpiresAt,
    patienceExpired,
    error,
    joinRefusal,
    enter,
    reconnect,
    ready,
    acceptBotOffer,
    leave,
    clearPendingLeave,
    reset,
  };
}

/** A refused socket, in words a player can act on (S2-4, unchanged). */
function authErrorMessage(code: number): string | null {
  switch (code) {
    case ROOM_AUTH_ERROR.MISSING_TOKEN:
    case ROOM_AUTH_ERROR.INVALID_TOKEN:
      return t("room.auth.expired");
    case ROOM_AUTH_ERROR.SUSPENDED:
      return t("room.auth.suspended");
    case ROOM_AUTH_ERROR.DELETED:
    case ROOM_AUTH_ERROR.UNKNOWN_PLAYER:
      return t("room.auth.unknownPlayer");
    case ROOM_AUTH_ERROR.AUTH_UNAVAILABLE:
      return t("room.auth.unavailable");
    default:
      return null;
  }
}

function joinErrorMessage(err: unknown): string {
  if (err instanceof ApiRequestError && err.status === 401) return t("room.auth.signedOut");
  const withCode = err as { code?: unknown; message?: string };
  if (typeof withCode.code === "number") {
    const mapped = authErrorMessage(withCode.code);
    if (mapped) return mapped;
  }
  return err instanceof Error ? err.message : String(err ?? "unknown");
}
